import React, { createContext, useContext, ReactNode } from 'react';
import { useCognito, CognitoAuthState, CognitoAuthActions } from '../hooks/useCognito';

// 認証コンテキストの型定義
interface AuthContextType extends CognitoAuthState, CognitoAuthActions {
  // 追加のヘルパーメソッド
  hasValidToken: boolean;
  userEmail: string | null;
}

// コンテキストの作成
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// プロバイダーのProps
interface AuthProviderProps {
  children: ReactNode;
}

// 認証プロバイダーコンポーネント
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const cognitoAuth = useCognito();

  // 追加のヘルパー値
  const hasValidToken = Boolean(cognitoAuth.idToken && cognitoAuth.isAuthenticated);
  
  // ユーザーメールの取得（Amplify v6対応）
  const userEmail = cognitoAuth.user?.signInDetails?.loginId || 
                   cognitoAuth.user?.username || 
                   null;

  const contextValue: AuthContextType = {
    ...cognitoAuth,
    hasValidToken,
    userEmail
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// カスタムフック：認証コンテキストを使用
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// 認証が必要なコンポーネントをラップするHOC
export const withAuth = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
      return (
        <div className="auth-loading">
          <div className="loading-spinner">認証状態を確認中...</div>
        </div>
      );
    }

    if (!isAuthenticated) {
      return (
        <div className="auth-required">
          <div className="auth-message">
            <h2>認証が必要です</h2>
            <p>このページを表示するにはログインが必要です。</p>
          </div>
        </div>
      );
    }

    return <Component {...props} />;
  };
};
